const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services-script';
const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const DRIVE_UPLOAD_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const DRIVE_AUTH_STORAGE_KEY = 'sanctum-google-drive-authorized';
const GOOGLE_DRIVE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID ||
  '302196231799-mdd4q21diigtsa6ngdmf9iqi1s3ftmha.apps.googleusercontent.com';
const GOOGLE_DRIVE_FOLDER_ID =
  process.env.REACT_APP_GOOGLE_DRIVE_FOLDER_ID ||
  '1CubOnwLvJthdF9oZhAud_Bgx6yfrTqVi';

let accessToken = null;
let accessTokenExpiry = 0;

function ensureDriveConfiguration() {
  if (!GOOGLE_DRIVE_CLIENT_ID || !GOOGLE_DRIVE_FOLDER_ID) {
    throw new Error('Google Drive is not configured. Add the Drive client ID and folder ID to your environment.');
  }
}

function loadGoogleIdentityScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve(window.google);
      return;
    }

    const existingScript = document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_IDENTITY_SCRIPT_ID;
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
    document.head.appendChild(script);
  });
}

async function requestDriveAccessToken() {
  ensureDriveConfiguration();
  await loadGoogleIdentityScript();

  if (accessToken && Date.now() < accessTokenExpiry - 60_000) {
    return accessToken;
  }

  const hasPreviousAuthorization = window.localStorage.getItem(DRIVE_AUTH_STORAGE_KEY) === 'true';

  try {
    return await requestTokenWithPrompt(hasPreviousAuthorization ? '' : 'consent');
  } catch (error) {
    if (hasPreviousAuthorization) {
      return requestTokenWithPrompt('consent');
    }

    throw error;
  }
}

function requestTokenWithPrompt(prompt) {
  return new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_DRIVE_CLIENT_ID,
      scope: DRIVE_UPLOAD_SCOPE,
      callback: (response) => {
        if (!response?.access_token) {
          reject(new Error('Google Drive authorization failed.'));
          return;
        }

        accessToken = response.access_token;
        accessTokenExpiry = Date.now() + (Number(response.expires_in) || 3600) * 1000;
        window.localStorage.setItem(DRIVE_AUTH_STORAGE_KEY, 'true');
        resolve(accessToken);
      },
      error_callback: () => {
        reject(new Error(prompt === '' ? 'Silent Google Drive authorization failed.' : 'Google Drive authorization was cancelled.'));
      },
    });

    tokenClient.requestAccessToken({ prompt });
  });
}

function dataUrlToBlob(dataUrl) {
  const [meta, data] = dataUrl.split(',');
  const mimeMatch = meta?.match(/data:(.*?);base64/);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';
  const binary = window.atob(data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
}

function toDriveFileUrl(fileId) {
  return `https://drive.google.com/uc?export=view&id=${fileId}`;
}

function toDrivePreviewUrl(fileId) {
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1600`;
}

async function createPublicPermission(fileId, token) {
  const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}/permissions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      role: 'reader',
      type: 'anyone',
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to make the Google Drive image shareable.');
  }
}

async function uploadSingleImage(image, token) {
  const blob = dataUrlToBlob(image.src);

  const metadata = {
    name: image.name || `sanctum-image-${Date.now()}.png`,
    mimeType: image.type || blob.type || 'image/png',
    parents: [GOOGLE_DRIVE_FOLDER_ID],
  };

  const boundary = `sanctum-boundary-${Math.random().toString(36).slice(2)}`;
  const metadataPart =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${JSON.stringify(metadata)}\r\n`;
  const fileHeader =
    `--${boundary}\r\n` +
    `Content-Type: ${metadata.mimeType}\r\n\r\n`;
  const closingBoundary = `\r\n--${boundary}--`;

  const requestBody = new Blob([metadataPart, fileHeader, blob, closingBoundary], {
    type: `multipart/related; boundary=${boundary}`,
  });

  const uploadResponse = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: requestBody,
    }
  );

  if (!uploadResponse.ok) {
    const errorData = await uploadResponse.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || `Failed to upload ${metadata.name} to Google Drive.`);
  }

  const uploadedFile = await uploadResponse.json();
  await createPublicPermission(uploadedFile.id, token);

  return {
    ...image,
    src: toDriveFileUrl(uploadedFile.id),
    previewSrc: toDrivePreviewUrl(uploadedFile.id),
    driveFileId: uploadedFile.id,
    storageProvider: 'google-drive',
  };
}

function isDriveHostedImage(image) {
  return image?.storageProvider === 'google-drive' || Boolean(image?.driveFileId);
}

export async function uploadImagesToGoogleDrive(images) {
  if (!Array.isArray(images) || images.length === 0) {
    return [];
  }

  const imagesToUpload = images.filter(
    (image) => !isDriveHostedImage(image) && typeof image?.src === 'string' && image.src.startsWith('data:')
  );
  if (imagesToUpload.length === 0) {
    return images;
  }

  const token = await requestDriveAccessToken();
  const uploadedImages = await Promise.all(images.map(async (image) => {
    if (!imagesToUpload.some((candidate) => candidate.id === image.id)) {
      return image;
    }

    return uploadSingleImage(image, token);
  }));

  return uploadedImages;
}
